import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import { displayName } from "@/lib/patientFormat";
import { completeVisit, terminateVisit } from "@/lib/telehealth";
import { fetchAppointments, openTelehealthSession, patientName, firstName, addDays, startOfDay } from "./thModel";

const LIMIT_MIN = 20, SNOOZE_MS = 10 * 60_000, KEY = "revq.th-snooze";
const readSnooze = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };
const mins = (a, now) => Math.floor((now - new Date(a.start_time)) / 60_000);
const chan = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("revq-th-long") : null;

const priorDay = (a) => a.start_time && new Date(a.start_time).toDateString() !== new Date().toDateString();
const since = (a) => `since ${new Date(a.start_time).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
const ageOf = (a, now) => (priorDay(a) ? since(a) : `${mins(a, now)} min`);

const BTN = "whitespace-nowrap inline-flex items-center h-7 px-2 rounded-[7px] text-[12px] font-semibold transition-colors disabled:opacity-50 hover:bg-[#F1F1F4]";
function RowConfirm({ a, kind, onKeep, onGo }) {
  const stop = kind === "stop";
  return (
    <div className="w-full flex flex-wrap items-center gap-2 mt-2 px-3 py-2 rounded-[8px]" style={{ background: stop ? "#FDEEED" : "#F6F6F8", border: `1px solid ${stop ? "#F4C7C3" : "#E9E9EC"}` }} data-testid={`th-long-confirm-${a.appointment_id}`}>
      <p className="flex-[1_1_260px] min-w-0 text-[12px] leading-[1.5]" style={{ color: stop ? "#7A1F1A" : "#141412" }}>
        <b>This ends the call. {firstName(a)} cannot rejoin — a new visit would have to be booked.</b>{" "}
        {stop ? <><b>No note is written</b>, and the recording is frozen out of transcription until the visit is set back to active.</> : "The recording is kept, and Lumina writes the SOAP note from it once it is ready."}
      </p>
      <button type="button" className={`${BTN} border`} style={{ borderColor: "#DCDCE1", color: "#141412", background: "#fff" }} onClick={onKeep} data-testid={`th-long-keep-${a.appointment_id}`}>Keep talking</button>
      <button type="button" className="whitespace-nowrap h-7 px-2.5 rounded-[7px] text-[12px] font-semibold text-white hover:opacity-90" style={{ background: stop ? "#FF3B30" : "#141412" }} onClick={onGo} data-testid={`th-long-go-${a.appointment_id}`}>{stop ? "End, no note" : "End & write the note"}</button>
    </div>
  );
}

function LongRow({ a, now, busy, act, conf, setConf }) {
  const id = a.appointment_id, b = busy === id, mine = conf?.id === id ? conf.kind : null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 border-b last:border-b-0" style={{ borderColor: "#F1F1F4" }} data-testid={`th-long-${id}`}>
      <div className="flex-1 min-w-[200px] flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="text-[13px] font-semibold" style={{ color: "#141412" }}>{patientName(a)}</span>
        <span className="text-[11.5px] font-semibold tabular-nums px-1.5 py-[1px] rounded-full" style={{ background: "#FEF7EC", border: "1px solid #F6DFC0", color: "#9A5A00" }} data-testid={`th-long-min-${id}`}>{ageOf(a, now)}</span>
        <span className="text-[11.5px]" style={{ color: "#6E6E6B" }}>{displayName(a.provider_name || "")}{a.visit_type_name ? `${a.provider_name ? " · " : ""}${a.visit_type_name}` : ""}</span>
      </div>
      <button type="button" className={`${BTN} text-[#141412]`} disabled={b} onClick={() => act.open(a)} data-testid={`th-long-open-${id}`}>Open</button>
      <button type="button" className={`${BTN} text-[#141412]`} disabled={b} onClick={() => setConf({ id, kind: "complete" })} data-testid={`th-long-complete-${id}`}>Complete & write note</button>
      <button type="button" className={`${BTN} text-[#141412]`} disabled={b} onClick={() => setConf({ id, kind: "stop" })} data-testid={`th-long-stop-${id}`}>Stop session</button>
      <button type="button" className={`${BTN} w-7 justify-center px-0 text-[#6E6E6B]`} onClick={() => act.snooze([a])} title="Snooze 10 min" aria-label={`Snooze ${patientName(a)} for 10 min`} data-testid={`th-long-snooze-${id}`}><Bell size={14} /></button>
      {mine ? <RowConfirm a={a} kind={mine} onKeep={() => setConf(null)} onGo={() => { setConf(null); (mine === "stop" ? act.stop : act.complete)(a); }} /> : null}
    </div>
  );
}

// App-wide slim red alert for telehealth visits running past 20 minutes since Start; list drops down on click.
export function LongVisitBanner() {
  const { selectedClientId: cid } = useClient();
  const qc = useQueryClient(), box = useRef(null);
  const [now, setNow] = useState(Date.now()), [snooze, setSnooze] = useState(readSnooze), [busy, setBusy] = useState(null), [open, setOpen] = useState(false), [conf, setConf] = useState(null);
  const { data = [], refetch } = useQuery({
    queryKey: ["th-long", cid], enabled: !!cid, refetchInterval: 15_000, refetchOnWindowFocus: true,
    queryFn: () => { const t = startOfDay(new Date()); return fetchAppointments({ cid, from: addDays(t, -7), to: t }); },
  });
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const sync = () => { setSnooze(readSnooze()); refetch(); };
    const onStore = (e) => { if (e.key === KEY) setSnooze(readSnooze()); };
    if (chan) chan.onmessage = sync;
    window.addEventListener("storage", onStore);
    return () => { if (chan) chan.onmessage = null; window.removeEventListener("storage", onStore); };
  }, [refetch]);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") { setConf(null); setOpen(false); } };
    document.addEventListener("mousedown", off); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, [open]);
  const long = data.filter((a) => a.status === "in_progress" && a.start_time && mins(a, now) >= LIMIT_MIN && !((snooze[a.appointment_id] || 0) > now))
    .sort((x, y) => new Date(x.start_time) - new Date(y.start_time));
  useEffect(() => { if (!long.length) setOpen(false); }, [long.length]);
  if (!long.length) return null;

  const done = (id) => {
    qc.setQueryData(["th-long", cid], (cur) => (cur || []).map((x) => (x.appointment_id === id ? { ...x, status: "completed" } : x)));
    ["th-long", "th-appts", "th-act"].forEach((k) => qc.invalidateQueries({ queryKey: [k, cid] }));
    chan?.postMessage("changed");
  };
  // Someone else may have closed it already: re-read the visit first and just drop it if so.
  const stillOpen = async (a) => {
    try {
      const { data: r } = await api.get(`/api/edi/televisit/appointments/${a.appointment_id}`);
      if ((r?.appointment || r)?.status === "in_progress") return true;
    } catch { return true; }
    toast.info(`${patientName(a)}'s visit was already closed. Removed from the list.`);
    done(a.appointment_id);
    return false;
  };
  const run = async (a, fn) => { setBusy(a.appointment_id); try { if ((await stillOpen(a)) && (await fn())) done(a.appointment_id); } finally { setBusy(null); } };
  const act = {
    open: async (a) => { setBusy(a.appointment_id); const ok = await stillOpen(a); setBusy(null); if (ok) { setOpen(false); openTelehealthSession(a); } },
    complete: (a) => run(a, async () => {
      try {
        if (a.visit_id) await api.post(`/api/edi/televisit/${a.visit_id}/event`, { event: "session_ended" }).catch(() => {});
        await completeVisit(a.appointment_id);
        toast.success(`${patientName(a)}'s visit is complete. The note is being written.`);
        qc.invalidateQueries({ queryKey: ["th-pipe"] });
        return true;
      } catch { toast.error("Couldn't complete the visit. Try again."); return false; }
    }),
    stop: (a) => run(a, async () => {
      try { await terminateVisit(a.appointment_id, a.visit_id); toast.success(`${patientName(a)}'s session ended. No note will be made.`); return true; }
      catch { toast.error("Couldn't end the session. Try again."); return false; }
    }),
    snooze: (xs) => { const s = { ...readSnooze() }; xs.forEach((a) => { s[a.appointment_id] = Date.now() + SNOOZE_MS; }); localStorage.setItem(KEY, JSON.stringify(s)); setSnooze(s); chan?.postMessage("snooze"); },
  };
  const n = long.length, first = long[0];
  const longest = long.some(priorDay) ? since(long.filter(priorDay).sort((x, y) => new Date(x.start_time) - new Date(y.start_time))[0]) : `${mins(first, now)} min`;
  return (
    <div ref={box} className="flex-none relative z-[30] px-4 pt-2" data-testid="th-long-banner">
      <div className="flex items-center gap-2 pl-3 pr-1.5 h-9 rounded-[10px]" style={{ background: "#FEF7EC", border: "1px solid #F6DFC0", color: "#9A5A00" }} role="alert" data-testid="th-long-bar">
        <AlertTriangle size={15} className="flex-none" />
        <span className="text-[13px] truncate min-w-0" data-testid="th-long-count">{n === 1
          ? <><b>{patientName(first)}'s visit has been running {priorDay(first) ? since(first) : `${mins(first, now)} min`}</b> · over {LIMIT_MIN} min, please close it</>
          : <><b>{n} visits running over {LIMIT_MIN} min</b> · longest {longest}</>}</span>
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="ml-auto flex-none inline-flex items-center gap-1 h-7 px-2.5 rounded-[7px] text-[12.5px] font-semibold text-white" style={{ background: "#141412" }} data-testid="th-long-review">Review<ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} /></button>
      </div>
      {open ? (
        <div className="absolute right-4 top-[calc(100%+6px)] w-[min(680px,calc(100%-32px))] rounded-[12px] bg-white overflow-hidden" style={{ border: "1px solid #E9E9EC", boxShadow: "0 14px 48px rgba(20,20,18,.14)" }} data-testid="th-long-list">
          <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: "#F1F1F4" }}>
            <span className="text-[11px] font-bold uppercase tracking-[.06em]" style={{ color: "#9B9B97" }}>Running over {LIMIT_MIN} min</span>
            {n > 1 ? <button type="button" className="ml-auto text-[12px] font-semibold hover:underline" style={{ color: "#6E6E6B" }} onClick={() => act.snooze(long)} data-testid="th-long-snooze-all">Snooze all 10 min</button> : null}
          </div>
          <div className="max-h-[320px] overflow-auto">{long.map((a) => <LongRow key={a.appointment_id} a={a} now={now} busy={busy} act={act} conf={conf} setConf={setConf} />)}</div>
        </div>
      ) : null}
    </div>
  );
}
